import moment from 'moment';
import {
    ITaskRoute,
    restGetTask,
    restGetTaskRoute,
} from '../../../../../services/tasks';
import { ITaskDetails } from '../../../../../state/types';
import { mapArrayToDictionary } from '../../../../../helpers/mapToDictionary';
import { SYSTEM_OBJECT_IDS } from '../../../../../constants/dictionaries/SystemObjects';

import { IAttribute } from '../../../../../features/Attributes/state';
import { TAttributeDictionary } from '../../../../../features/Attributes/types';
import { prepareAttributesFormData } from '../../../../../features/Attributes/utils';

import { ITaskForm } from '../../../../../state/ui/forms';

export const prepareTaskData = (
    task: ITaskDetails,
    routeData: ITaskRoute | null
) => {
    return {
        ...task,
        notice: task.notice || '',
        plannedOn: task.plannedOn || null,
        noDate: !task.plannedOn,
        plannedTimeFrom: task.plannedTimeFrom
            ? moment(task.plannedTimeFrom, 'HH:mm')
            : undefined,
        plannedTimeTo: task.plannedTimeTo
            ? moment(task.plannedTimeTo, 'HH:mm')
            : undefined,
        location: {
            label: task.location?.name || '',
            id: task.location?.id,
        },
        vehicle: { label: task.vehicleName || '', id: task.vehicleId },
        employee: { label: task.employeeName || '', id: task.employeeId },
        route: { id: routeData?.id || '', label: routeData?.name || '' },
        activityCategory: task.activityCategoryId ?? '',
        objectCategory: task.objectCategoryId ?? '',
        serviceType: task.serviceTypeId ?? '',
        serviceClass: task.serviceClassId ?? '',
        unitsCount: task.unitsCount,
        attachments: { files: [], errors: false },
        readOnlyFiles: task.attachments || [],
        externalLinkages: task.externalLinkages || [],
    };
};

export const processAttributes = (
    taskData: ITaskDetails,
    attrData: IAttribute[],
    setFieldValue: (fieldname: string, v: any) => void,
    setLoading: (bool: boolean) => void
) => {
    const dataAttributes = mapArrayToDictionary(
        taskData.attributes || [],
        'name',
        'value'
    );

    const attributes = prepareAttributesFormData(
        attrData,
        SYSTEM_OBJECT_IDS.Task,
        dataAttributes
    );

    setFieldValue('attributes', attributes);
    setLoading(false);
};

export const fetchTaskEditData = (
    attributePromise: Promise<IAttribute[]>,
    setFieldValue: (fieldname: string, v: any) => void,
    taskId: number,
    formValues: { data: ITaskForm; attributes: TAttributeDictionary },
    setLoading: (bool: boolean) => void
) => {
    Promise.all([
        restGetTask(taskId),
        attributePromise,
        restGetTaskRoute(taskId).catch((error) => {
            if (error?.status === 404) {
                return null;
            }
            throw error;
        }),
    ]).then(([taskData, attrData, routeData]) => {
        const task = prepareTaskData(taskData, routeData);
        setFieldValue('data', {
            ...formValues.data,
            ...task,
        });
        processAttributes(taskData, attrData, setFieldValue, setLoading);
    });
};

export const fetchTaskCopyData = (
    attributePromise: Promise<IAttribute[]>,
    setFieldValue: (fieldname: string, v: any) => void,
    taskId: number,
    formValues: { data: ITaskForm; attributes: TAttributeDictionary },
    setLoading: (bool: boolean) => void
) => {
    Promise.all([restGetTask(taskId), attributePromise]).then(
        ([taskData, attrData]) => {
            const {
                notice,
                activityCategoryId,
                objectCategoryId,
                serviceTypeId,
                serviceClassId,
                location,
                unitsCount,
                externalLinkages,
                rfidCode,
            } = taskData;
            const task = {
                notice,
                plannedOn: null,
                noDate: false,
                plannedTimeFrom: undefined,
                plannedTimeTo: undefined,
                location: {
                    label: location?.name,
                    id: location?.id,
                },
                status: 1,
                vehicle: { label: '' },
                employee: { label: '' },
                activityCategory: activityCategoryId ?? '',
                objectCategory: objectCategoryId ?? '',
                serviceType: serviceTypeId ?? '',
                serviceClass: serviceClassId ?? '',
                unitsCount,
                attachments: { files: [], errors: false },
                readOnlyFiles: [],
                externalLinkages,
                route: { label: '' },
                rfidCode,
            };
            setFieldValue('data', {
                ...formValues.data,
                ...task,
            });

            processAttributes(taskData, attrData, setFieldValue, setLoading);
        }
    );
};
